import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ExternalLink } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCMS } from '../context/CMSContext';
import { successStories as defaultStories } from '../data/mockData';

const StoryCard = ({ story, index, featured }) => (
  <motion.article
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ delay: index * 0.06, duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
    className={`group relative overflow-hidden rounded-2xl border border-white/5 hover:border-white/20 transition-colors duration-500 ${featured ? 'md:col-span-2' : ''}`}
    data-testid={`story-card-${story.slug}`}
  >
    <Link to={`/success-stories/${story.slug}`} className="block">
      <div className={`relative ${featured ? 'aspect-[16/8]' : 'aspect-[16/11]'} overflow-hidden`}>
        <img src={story.image} alt={`${story.client} — ${story.title}`}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
          loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050914] via-[#050914]/40 to-transparent" />
        {story.liveUrl && (
          <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] text-white font-bold bg-[#FF6B35]/90 backdrop-blur">
            <ExternalLink className="w-3 h-3" /> Live Preview
          </div>
        )}
        {/* Metric reveal on hover */}
        <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div className="flex flex-wrap gap-2">
            {(story.services || []).slice(0, 3).map((s) => (
              <span key={s} className="text-[10px] uppercase tracking-[0.2em] text-white font-bold border border-white/25 px-2.5 py-1 rounded-full backdrop-blur">
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="p-6 lg:p-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-bold mb-3">
          {story.industry} · {story.client}
        </div>
        <h3 className={`font-display font-medium tracking-tight text-white leading-snug ${featured ? 'text-2xl lg:text-4xl' : 'text-xl lg:text-2xl'}`}>
          {story.title}
        </h3>
        <p className="mt-3 text-white/50 text-sm font-light leading-relaxed line-clamp-2">
          {story.summary}
        </p>
        <div className="mt-6 inline-flex items-center gap-2 text-white text-sm font-semibold">
          <span className="border-b border-white/30 group-hover:border-white pb-0.5 transition-colors">View case study</span>
          <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
        </div>
      </div>
    </Link>
  </motion.article>
);

const SuccessStoriesPage = () => {
  const { get } = useCMS();
  const stories = get('success_stories') || defaultStories;
  const industries = useMemo(() => {
    const set = new Set(stories.map((s) => s.industry));
    return ['All', ...Array.from(set)];
  }, [stories]);
  const [filter, setFilter] = useState('All');
  const filtered = filter === 'All' ? stories : stories.filter((s) => s.industry === filter);

  return (
    <div className="min-h-screen bg-[#050914]">
      <Navbar />

      <section className="relative pt-36 pb-14 lg:pt-44 lg:pb-20 overflow-hidden" data-testid="stories-header">
        <div className="absolute inset-0 hero-mesh" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <p className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-6">
            <span className="w-8 h-px bg-white/30" /> Success Stories
          </p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter text-white leading-[1.02] max-w-4xl">
            Selected work from our partners.
          </h1>
          <p className="mt-7 text-white/60 text-lg max-w-2xl font-light leading-relaxed">
            Every project below was shipped by the Digitize team. Verified performance numbers are added as we validate outcomes with each client.
          </p>
        </div>
      </section>

      {/* Filter chips */}
      <section className="relative pb-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap gap-2 items-end" role="tablist" aria-label="Filter by industry">
          {industries.map((ind) => (
            <button key={ind} onClick={() => setFilter(ind)}
              role="tab" aria-selected={filter === ind}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-bold transition-all duration-300 ${
                filter === ind
                  ? 'bg-white text-[#050914] -translate-y-2 shadow-lg shadow-black/40'
                  : 'border border-white/10 text-white/60 hover:text-white hover:border-white/30 translate-y-0'
              }`}
              data-testid={`stories-filter-${ind.toLowerCase().replace(/\W+/g, '-')}`}>
              {ind}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="relative pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          {filtered.map((s, i) => (
            <StoryCard key={s.id} story={s} index={i} featured={i === 0} />
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-white/50 mt-16">No stories in this industry yet.</p>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default SuccessStoriesPage;
